import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLE_LABELS = {
  owner: "Propietario",
  admin: "Administrador",
  compliance_manager: "Responsable de cumplimiento",
  auditor: "Auditor",
  member: "Miembro",
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/preview/${token}`);
        setPreview(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Invitación no válida.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/invitations/accept", { token, name, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo aceptar la invitación.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm" data-testid="accept-card">
        <div className="text-[20px] font-semibold tracking-tight text-slate-900 mb-1">Nimbus</div>
        <h1 className="text-[16px] font-semibold text-slate-900 mb-4">Aceptar invitación</h1>

        {loading && <div className="text-slate-500 text-[14px]">Cargando invitación...</div>}

        {!loading && error && !preview && (
          <div data-testid="accept-error-final" className="text-destructive text-[14px]">{error}</div>
        )}

        {!loading && preview && !done && (
          <>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 text-[14px]">
              <div className="text-slate-500">Has sido invitado a unirte a</div>
              <div className="font-semibold text-slate-900 mt-1">{preview.organization_name}</div>
              <div className="mt-3 text-slate-500">Correo</div>
              <div className="text-slate-900">{preview.email}</div>
              <div className="mt-3 text-slate-500">Rol propuesto</div>
              <div className="text-slate-900">{ROLE_LABELS[preview.role] || preview.role}</div>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="accept-form">
              <div>
                <Label htmlFor="acc-name">Nombre completo</Label>
                <Input id="acc-name" data-testid="accept-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="acc-pass">Contraseña</Label>
                <Input id="acc-pass" data-testid="accept-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="acc-conf">Confirmar contraseña</Label>
                <Input id="acc-conf" data-testid="accept-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              {error && <div data-testid="accept-error" className="text-[12px] text-destructive">{error}</div>}
              <Button type="submit" disabled={submitting} className="w-full" data-testid="accept-submit">
                {submitting ? "Creando..." : "Aceptar invitación"}
              </Button>
            </form>
          </>
        )}

        {done && (
          <div data-testid="accept-success" className="text-[14px] text-slate-700">
            Cuenta creada correctamente. Redirigiendo a iniciar sesión...
          </div>
        )}
      </div>
    </div>
  );
}
